// frontend/src/components/staff/AvailabilityPeekStrip.js
//
// NEW-SHIFT-AVAILABILITY-PEEK — Inline availability mini-strip rendered inside
// the New / Edit Shift modal between the Staff dropdown and the Day / Start /
// End row.
//
// Visual spec: docs/design/NEW-SHIFT-AVAILABILITY-PEEK-design.md (Zuri,
// Option C — contextual mini-strip). Shows ONLY the day relevant to the shift
// being created (not the full 7-day matrix).
//
// Data source: in-prop `availability` array passed down from
// ScheduleBuilder.fetchShifts (which already loads availability for the
// visible week alongside shifts). No new fetch — the data is in-tree, filter
// client-side by (staffProfileId, dateYmd).
//
// Marker vocabulary borrowed from SCHED-AVAIL-DASHBOARD-002-B:
//   - Recurring window: filled `--brand-teal` circle (●)
//   - One-off window:   hollow `--brand-teal` ring   (○)
//
// States covered per Zuri spec §3.2:
//   - State A: windows visible (default happy path)
//   - State B: no avail for this day-of-week
//   - State C: no records at all
//   - State D: no staff selected
//
// State E (loading) + State F (fetch error) are NOT applicable in v1 because
// the data is in-prop (no fetch). If a future iteration moves this to a
// per-staff fetch (Marcus tech doc §"API Contracts" — extending the
// dashboard endpoint with `staffProfileId` filter), States E + F become
// active. The component reserves the data-testids for forward compatibility.

import React from "react";
import moment from "moment-timezone";
import {
  STUDIO_TZ,
  STUDIO_TZ_LABEL,
  getAvailabilityWindowsForDate,
  hasAnyAvailabilityRecords,
  resolveStaffDisplayName,
} from "./scheduleBuilderUtils";
import "./AvailabilityPeekStrip.css";

const formatTimeRange = (startHHMM, endHHMM) => {
  const start = moment(startHHMM, "HH:mm");
  const end = moment(endHHMM, "HH:mm");
  if (!start.isValid() || !end.isValid()) {
    return `${startHHMM} - ${endHHMM}`;
  }
  return `${start.format("h:mm A")} - ${end.format("h:mm A")} ${STUDIO_TZ_LABEL}`;
};

const formatHeaderDate = (dayYmd) => {
  const m = moment.tz(dayYmd, "YYYY-MM-DD", STUDIO_TZ);
  if (!m.isValid()) return "";
  return m.format("ddd, MMM D");
};

const dayNamePlural = (dayYmd) => {
  const m = moment.tz(dayYmd, "YYYY-MM-DD", STUDIO_TZ);
  if (!m.isValid()) return "this day";
  return `${m.format("dddd")}s`;
};

/**
 * AvailabilityPeekStrip
 *
 * @param {object} props
 * @param {string} props.staffProfileId   — selected staff's `StaffProfile._id` (string)
 * @param {object|null} props.staffProfile — full StaffProfile record (for display name + Set-availability CTA)
 * @param {string} props.dayYmd            — selected date in YYYY-MM-DD format (Phoenix-MST)
 * @param {Array}  props.availability      — full availability list (filtered in-component)
 * @param {() => void} [props.onSetAvailabilityClick] — invoked when the State C
 *                                                     "Set availability" CTA is
 *                                                     clicked. Parent opens the
 *                                                     §10K AdminSetAvailabilityModal.
 *                                                     If omitted, the CTA is hidden
 *                                                     (defense-in-depth — never
 *                                                     dangle a no-op link).
 */
const AvailabilityPeekStrip = ({
  staffProfileId,
  staffProfile,
  dayYmd,
  availability,
  onSetAvailabilityClick,
}) => {
  const headerDate = formatHeaderDate(dayYmd);
  const safeFullName = staffProfile ? resolveStaffDisplayName(staffProfile) : "";
  const firstName = safeFullName ? safeFullName.split(/\s+/)[0] : "this staff";

  // ---- State D: no staff selected ----------------------------------------
  if (!staffProfileId) {
    return (
      <section
        className="avail-peek-strip avail-peek-strip--no-staff"
        aria-labelledby="avail-peek-strip-header"
        data-testid="avail-peek-strip"
        data-state="no-staff"
      >
        <h3
          id="avail-peek-strip-header"
          className="avail-peek-strip__header"
        >
          {`Availability for ${headerDate}:`}
        </h3>
        <p className="avail-peek-strip__body avail-peek-strip__body--muted">
          Pick a staff member to see availability
        </p>
      </section>
    );
  }

  // Has any records at all? Determines State B vs C.
  const hasAnyRecords = hasAnyAvailabilityRecords({
    staffProfileId,
    availability,
  });

  // Windows that apply to (staff, this date)
  const windows = getAvailabilityWindowsForDate({
    staffProfileId,
    dateYmd: dayYmd,
    availability,
  });

  // ---- State C: zero records at all ---------------------------------------
  if (!hasAnyRecords) {
    return (
      <section
        className="avail-peek-strip avail-peek-strip--no-records"
        aria-labelledby="avail-peek-strip-header"
        data-testid="avail-peek-strip"
        data-state="no-records"
      >
        <h3
          id="avail-peek-strip-header"
          className="avail-peek-strip__header"
        >
          {`Availability for ${headerDate}:`}
        </h3>
        <p className="avail-peek-strip__body avail-peek-strip__body--muted">
          {`No availability set yet for ${firstName}`}
          {typeof onSetAvailabilityClick === "function" ? (
            <>
              {"  "}
              <button
                type="button"
                className="avail-peek-strip__cta-link"
                onClick={onSetAvailabilityClick}
                aria-label={`Set availability for ${safeFullName || firstName}`}
                data-testid="avail-peek-strip-set-avail-cta"
              >
                Set availability ›
              </button>
            </>
          ) : null}
        </p>
      </section>
    );
  }

  // ---- State B: has records, but none on this dayOfWeek -------------------
  if (windows.length === 0) {
    return (
      <section
        className="avail-peek-strip avail-peek-strip--no-day"
        aria-labelledby="avail-peek-strip-header"
        data-testid="avail-peek-strip"
        data-state="no-day"
      >
        <h3
          id="avail-peek-strip-header"
          className="avail-peek-strip__header"
        >
          {`Availability for ${headerDate}:`}
        </h3>
        <p className="avail-peek-strip__body avail-peek-strip__body--muted">
          {`— No availability on ${dayNamePlural(dayYmd)}`}
        </p>
      </section>
    );
  }

  // ---- State A: windows visible (happy path) ------------------------------
  return (
    <section
      className="avail-peek-strip avail-peek-strip--windows"
      aria-labelledby="avail-peek-strip-header"
      data-testid="avail-peek-strip"
      data-state="windows"
    >
      <h3
        id="avail-peek-strip-header"
        className="avail-peek-strip__header"
      >
        {`Availability for ${headerDate}:`}
      </h3>
      <ul className="avail-peek-strip__windows">
        {windows.map((w, idx) => {
          const isOneOff = w.isRecurring === false;
          const range = formatTimeRange(w.startTime, w.endTime);
          const ariaLabel = isOneOff
            ? `One-off availability: ${range}`
            : `Recurring availability: ${range}`;
          return (
            <li
              key={w._id || `${w.startTime}-${w.endTime}-${idx}`}
              className={
                "avail-peek-strip__window" +
                (isOneOff
                  ? " avail-peek-strip__window--one-off"
                  : " avail-peek-strip__window--recurring")
              }
              aria-label={ariaLabel}
              data-testid="avail-peek-strip-window"
              data-window-kind={isOneOff ? "one-off" : "recurring"}
            >
              <span
                className={
                  "avail-peek-strip__marker" +
                  (isOneOff
                    ? " avail-peek-strip__marker--one-off"
                    : " avail-peek-strip__marker--recurring")
                }
                aria-hidden="true"
              />
              <span className="avail-peek-strip__time">{range}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default AvailabilityPeekStrip;
